import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogOut, FileDown, CalendarDays, Receipt, FilePen } from "lucide-react";
import { CLINIC } from "@/lib/brand";

export default function PatientPortal() {
  const { user, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [rx, setRx] = useState([]);
  const [inv, setInv] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [a, p, i] = await Promise.all([
        api.get("/appointments"),
        api.get("/prescriptions"),
        api.get("/invoices"),
      ]);
      setAppts(a.data); setRx(p.data); setInv(i.data);
    } catch (err) {
      console.error("Failed to load portal data:", err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="min-h-screen bg-[color:var(--bg-marketing)]" data-testid="patient-portal">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 md:px-10 h-18 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={CLINIC.logoUrl} alt={CLINIC.name} className="w-11 h-11 rounded-full object-cover ring-1 ring-[color:var(--primary-light)]" />
            <span className="font-heading text-[15px] font-medium leading-tight">{CLINIC.shortName}<br /><span className="text-[10px] text-[color:var(--muted)] tracking-[0.18em] uppercase font-normal">Patient Portal</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[color:var(--muted)] hidden sm:inline">Hi, {user?.name?.split(" ")[0]}</span>
            <button onClick={logout} className="text-sm text-[color:var(--muted)] hover:text-[color:var(--ink)] flex items-center gap-1.5" data-testid="portal-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-14">
        <h1 className="font-heading text-3xl md:text-4xl font-medium mb-1">Your care, in one place.</h1>
        <p className="text-[color:var(--muted)] mb-8">Manage appointments, view prescriptions and download invoices.</p>

        <Link to="/book" className="inline-block mb-10">
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="portal-book-new">
            Book a new appointment
          </Button>
        </Link>

        <Section icon={CalendarDays} title="Upcoming appointments" empty="No appointments scheduled.">
          {appts.map((a) => (
            <div key={a.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5 flex justify-between items-center" data-testid={`portal-apt-${a.id}`}>
              <div>
                <p className="font-medium text-[color:var(--ink)]">{new Date(a.scheduled_at).toLocaleString()}</p>
                <p className="text-sm text-[color:var(--muted)]">{a.reason || "—"} · <span className="capitalize">{a.mode?.replace("_"," ")}</span></p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-[color:var(--primary-light)]/50 text-[color:var(--primary-hover)] capitalize">{a.status}</span>
            </div>
          ))}
        </Section>

        <Section icon={FilePen} title="Prescriptions" empty="No prescriptions yet.">
          {rx.map((r) => (
            <div key={r.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5 flex justify-between items-center" data-testid={`portal-rx-${r.id}`}>
              <div>
                <p className="font-heading text-lg text-[color:var(--ink)]">{r.remedy} <span className="text-[color:var(--primary)]">{r.potency}</span></p>
                <p className="text-sm text-[color:var(--muted)]">{r.dosage} · {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
              <a href="#" onClick={async (ev) => { ev.preventDefault(); const resp = await api.get(`/prescriptions/${r.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(resp.data), "_blank"); }}>
                <Button variant="outline" size="sm" className="rounded-full text-xs" data-testid={`portal-rx-pdf-${r.id}`}><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
              </a>
            </div>
          ))}
        </Section>

        <Section icon={Receipt} title="Invoices" empty="No invoices yet.">
          {inv.map((v) => (
            <div key={v.id} className="bg-white border border-[color:var(--border)] rounded-2xl p-5 flex justify-between items-center" data-testid={`portal-inv-${v.id}`}>
              <div>
                <p className="font-medium text-[color:var(--ink)]">Invoice · ₹{v.total?.toLocaleString()}</p>
                <p className="text-sm text-[color:var(--muted)]">{new Date(v.created_at).toLocaleDateString()} · Balance ₹{v.balance?.toLocaleString()}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${v.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{v.status}</span>
            </div>
          ))}
        </Section>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children, empty }) {
  const items = Array.isArray(children) ? children : [children].filter(Boolean);
  return (
    <section className="mb-10">
      <h2 className="font-heading text-xl text-[color:var(--ink)] font-medium mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-[color:var(--primary)]" strokeWidth={1.6} /> {title}
      </h2>
      {items.length ? <div className="space-y-3">{items}</div> : <p className="text-sm text-[color:var(--muted)] bg-white border border-[color:var(--border)] rounded-2xl py-10 text-center">{empty}</p>}
    </section>
  );
}
